import React, { useEffect, useMemo, useRef } from 'react';
import { Form, Card, Alert, Typography, Tag, Row, Col, Space, Select } from 'antd';
import { ProductSelectionFormProps } from '../types';
import { useOnboardingStore } from '../stores/onboarding-store';
import { CheckCircleOutlined } from '@ant-design/icons';
import type { CustomerCategoryProductDTO, ProductDTO } from '@/shared/api/model';

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;

type SelectableProduct = Partial<CustomerCategoryProductDTO & ProductDTO> & { productCode?: string };

const ProductSelectionForm: React.FC<ProductSelectionFormProps> = ({
  categoryProducts,
  selectedProducts,
  onProductsChange,
  allProducts,
  allowAdditionalSelection,
  errors = {},
}) => {
  const [form] = Form.useForm();
  const { markStepCompleted } = useOnboardingStore();
  const hasInitialized = useRef(false);

  const getProductKey = (product: SelectableProduct | undefined | null) =>
    product?.glNo || product?.productCode || '';

  const normalizeProduct = (product: SelectableProduct) => ({
    productCode: product.productCode || product.glNo,
    glNo: product.glNo || product.productCode,
    productName: product.productName || product.product || product.glNo,
    productType: product.productType,
    productTypeName: product.productTypeName,
    isDefault: product.isDefault,
    displayOrder: product.displayOrder,
  });

  const mergeProducts = (products: SelectableProduct[]) => {
    const seen = new Set<string>();
    return products
      .filter((product) => {
        const key = getProductKey(product);
        if (!key || seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      })
      .map(normalizeProduct);
  };

  const additionalProductOptions = useMemo(
    () => (allProducts || []).filter((product) => {
      const key = getProductKey(product);
      return !!key && !categoryProducts.some((categoryProduct) => getProductKey(categoryProduct) === key);
    }),
    [allProducts, categoryProducts],
  );

  useEffect(() => {
    if (categoryProducts && categoryProducts.length > 0 && !hasInitialized.current) {
      const mergedProducts = mergeProducts([...(selectedProducts || []), ...categoryProducts]);
      onProductsChange(mergedProducts);
      markStepCompleted(4);
      hasInitialized.current = true;
    }
  }, [categoryProducts, selectedProducts, onProductsChange, markStepCompleted]);

  const handleAdditionalProductsChange = (productKeys: string[]) => {
    const categoryAndExistingProducts = mergeProducts([...(selectedProducts || []), ...categoryProducts]);
    const additionalProducts = productKeys
      .map((key) => allProducts?.find((product) => getProductKey(product) === key))
      .filter((product): product is ProductDTO => !!product)
      .map(normalizeProduct);

    onProductsChange(mergeProducts([...categoryAndExistingProducts, ...additionalProducts]));
  };

  const renderProductCard = (product: CustomerCategoryProductDTO) => {
    if (!product) return null;

    const isCategoryProduct = categoryProducts.some((categoryProduct) => getProductKey(categoryProduct) === getProductKey(product));

    return (
      <Col key={product.id} xs={24} sm={12} md={8} lg={6}>
        <Card
          className="product-card included"
          style={{ 
            borderColor: '#52c41a',
            backgroundColor: '#f6ffed'
          }}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong>{product.productName}</Text>
              <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
            </div>
            
            <Space wrap>
              <Tag color="blue">{product.productType}</Tag>
              {product.isRequired && <Tag color="red">Required</Tag>}
              {product.isDefault && <Tag color="green">Default</Tag>}
            </Space>
            
            {product.glNo && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                GL: {product.glNo}
              </Text>
            )}
            
            <Tag color="success" style={{ marginTop: '8px' }}>
              {isCategoryProduct ? '✓ Automatically Included' : '✓ Added Manually'}
            </Tag>
          </Space>
        </Card>
      </Col>
    );
  };

  // Products are passed from parent, no loading or error states needed

  if ((!categoryProducts || categoryProducts.length === 0) && !allowAdditionalSelection) {
    return (
      <Alert
        message="No Products Configured"
        description="No products are configured for this customer category. The customer will be created without any products."
        type="info"
        showIcon
      />
    );
  }

  return (
    <div className="product-selection-form">
      <Form form={form} layout="vertical">
        <div className="product-selection-header">
          <Title level={4}>Products & Services</Title>
          <Alert
            message={allowAdditionalSelection
              ? 'Category products are assigned automatically, and you can add more products below'
              : 'All products configured for this category will be automatically assigned to the customer'}
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Paragraph type="secondary">
            {allowAdditionalSelection
              ? 'Default category products are included automatically, and any extra products you add will also be saved.'
              : 'The following products will be included for this customer:'}
          </Paragraph>
        </div>

        {allowAdditionalSelection && (
          <Form.Item label="Add More Products">
            <Select
              mode="multiple"
              placeholder="Select additional products"
              value={selectedProducts
                .map((product) => getProductKey(product))
                .filter((key) => !categoryProducts.some((categoryProduct) => getProductKey(categoryProduct) === key))}
              onChange={handleAdditionalProductsChange}
              optionFilterProp="children"
              showSearch
              allowClear
            >
              {additionalProductOptions.map((product) => (
                <Option key={getProductKey(product)} value={getProductKey(product)}>
                  {product.productName || product.product || product.glNo}
                  {product.glNo ? ` (${product.glNo})` : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Row gutter={[16, 16]}>
          {selectedProducts
            .slice()
            .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
            .map((product, index) => renderProductCard({
              id: index,
              glNo: product.glNo,
              productName: product.productName,
              isDefault: product.isDefault,
              productType: product.productType,
              productTypeName: product.productTypeName,
              displayOrder: product.displayOrder,
            }))}
        </Row>

        <div className="selection-summary" style={{ marginTop: 24 }}>
          <Alert
            message={`${selectedProducts.length} product${selectedProducts.length !== 1 ? 's' : ''} will be assigned to this customer`}
            type="info"
            showIcon
          />
          
          {errors.products && (
            <Alert
              message={errors.products}
              type="error"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </div>
      </Form>
    </div>
  );
};

export default ProductSelectionForm;
